<template>
  <div class="scenario-container muzik-scenario mk-container">
    <div class="pgnnp-header">
      <Paganiniplus />
    </div>
    <Breadcrumbs :breadcrumbs="breadcrumbs" />
    <div v-if="loaded">
      <div v-if="scenarioData && scenarioData.id" class="wrapper">
        <div v-if="scenarioData.file_banner_link !== ''"
             :style="{ backgroundImage: 'url(' + scenarioData.file_banner_link + ')', backgroundSize: 'cover' }"
             class="scenario-banner"
        >
          <!-- <img v-img-loader="scenarioData.file_banner_link"> -->
        </div>
        <div v-if="scenarioData.id" class="scenario-content">
          <h1 class="title">
            {{ displayLocaleTitle(scenarioData) }}
          </h1>
          <a class="option" @click="toggleShareModal">
            <i class="material-icons">
              share
            </i>
          </a>
          <p class="desp">
            <span v-if="displayLocaleDescription(scenarioData)" v-html="displayLocaleDescription(scenarioData)">
              <!-- {{ displayLocaleDescription(scenarioData) }} -->
            </span>
          </p>
        </div>
        <div v-if="scenarioData.official_playlists" class="player-list">
          <ul>
            <li v-for="playlist in scenarioData.official_playlists" :key="playlist.id">
              <template v-if="playlist.playlist">
                <RouterLink :to="{name:'playlistById', params: { locale: $i18n.localePath(), id: playlist.playlist.id }}" class="block-img" tag="div">
                  <img v-img-lazy-loader :data-hide="true"
                       :data-src="playlist.playlist.file_link"
                       :data-default-src="'/img/img-default/img-playlist-default-l.png'" alt="playlist image"
                  >
                </RouterLink>
                <RouterLink :to="{name:'playlistById', params: { locale: $i18n.localePath(), id: playlist.playlist.id }}" class="title" tag="p">
                  {{ displayScenarioPlaylistLocaleTitle(playlist) }}
                </RouterLink>
              </template>
            </li>
          </ul>
        </div>
        <div class="pgnnp-footer">
          <Paganiniplus />
        </div>
      </div>
      <CommonDummy v-else />
    </div>
    <div v-else class="loader-container">
      <Loader />
    </div>
    <Modal v-if="shareModal"
           class="mk-modal mk-modal--share"
           @close="shareModal = false"
    >
      <slot>
        <SocialShare
          v-show="scenarioData.id"
          :title="title"
          :cover="scenarioData.file_cover_link"
          :share-link="shareLink"
        />
      </slot>
    </Modal>
  </div>
</template>

<script>
import Paganiniplus from '@/components/tsp/Paganiniplus'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import Loader from '@/components/common/Loader'
import Modal from '@/components/common/Modal'
import SocialShare from '@/components/common/SocialShare'
import CommonDummy from '@/components/common/DummyStatus/CommonDummy'
import ImgLazyLoader from '@/directives/ImgLazyLoader'
import { displayLocaleTitle, displayLocaleDescription, displayScenarioPlaylistLocaleTitle } from '@/assets/helpers/ScenarioHelper.js'
import { displayLocaleTitle as displayPlaylistLocaleTitle } from '@/assets/helpers/PlaylistHelper.js'
import ScenarioService from '@/assets/services/ScenarioService'

export default {
  name: 'ScenarioById',
  components: {
    Paganiniplus,
    Breadcrumbs,
    Loader,
    Modal,
    SocialShare,
    CommonDummy
  },
  directives: {
    ImgLazyLoader
  },
  props: {},
  data() {
    return {
      loaded: false,
      shareModal: false,
      scenarioData: {}
    }
  },
  metaInfo() {
    return {
      title: this.title,
      meta: [
        ...this.$route.meta.metaInfo.meta,
        { vmid: 'name', name: 'name', content: this.title },
        { vmid: 'description', name: 'description', content: this.description },
        { vmid: 'keywords', name: 'keywords', content: this.keywords },
        { vmid: 'image', name: 'image', content: this.cover },
        { vmid: 'og:title', name: 'og:title', content: this.title },
        { vmid: 'og:description', name: 'og:description', content: this.description },
        { vmid: 'og:image', name: 'og:image', content: this.cover }
      ],
      link: [
        { vmid: 'canonical', rel: 'canonical', href: this.canonicalURL },
        { vmid: 'image_src', rel: 'image_src', href: this.cover }
      ],
      __dangerouslyDisableSanitizers: ['script'],
      script: [{ innerHTML: JSON.stringify(this.structuredData), type: 'application/ld+json' }]
    }
  },
  computed: {
    defaultImage() {
      return require('@/assets/images/img-playlist/img-playlist-default-l.png')
    },
    locale() {
      return this.$i18n.locale.toLowerCase()
    },
    shareLink() {
      return `${window.location.protocol}//${window.location.host}/${this.$i18n.localePath()}/scenarios/${this.scenarioData.id}`
    },
    breadcrumbs() {
      let breadcrumbsAry = [
        { routeName: 'index', localeLabel: 'LISTEN.TITLE', redirectPath: '' }
      ]
      const localeTitle = this.displayLocaleTitle(this.scenarioData)
      if (localeTitle) {
        breadcrumbsAry.push({ routeName: '', localeLabel: localeTitle, redirectPath: '' })
      } else {
        breadcrumbsAry.push({ routeName: '', localeLabel: 'COMMON.SCENARIO', redirectPath: '' })
      }
      return breadcrumbsAry
    },
    // default meta data, update after fetched scenario
    title() {
      const titleText = this.displayLocaleTitle(this.scenarioData)
      return titleText
        ? `${titleText} | ${this.$t('SCENARIO.TITLE')} | ${this.$t('LISTEN.TITLE')} | MUZIK AIR`
        : `${this.$t('SCENARIO.TITLE')} | MUZIK AIR`
    },
    description() {
      const descriptionText = this.displayLocaleDescription(this.scenarioData)
      return descriptionText || this.$t('SCENARIO.TITLE')
    },
    keywords() {
      let keywordsAry = []
      keywordsAry.push(this.$t('SCENARIO.TITLE'))
      keywordsAry.push(this.displayLocaleTitle(this.scenarioData))
      // keywordsAry.push(this.displayLocaleDescription(this.scenarioData))
      if (this.scenarioData && this.scenarioData.official_playlists) {
        this.scenarioData.official_playlists.forEach(playlist => {
          keywordsAry.push(this.displayScenarioPlaylistLocaleTitle(playlist))
        })
      }
      return keywordsAry.join(', ')
    },
    cover() {
      return this.scenarioData && this.scenarioData.file_cover_link
        ? this.scenarioData.file_cover_link
        : `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/img/cover-social/listen.png`
    },
    canonicalURL() {
      return this.scenarioData && this.scenarioData.id
        ? `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/scenarios/${this.scenarioData.id}`
        : `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}`
    },
    structuredData() {
      const structuredData = [
        {
          '@context': 'http://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            {
              '@type': 'ListItem',
              'position': 1,
              'item': {
                '@id': `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/`,
                'name': this.$t('LISTEN.TITLE')
              }
            }
          ]
        }
      ]
      if (this.scenarioData && this.scenarioData.official_playlists) {
        const scenarioStructuredData = {
          '@context': 'http://schema.org',
          '@type': 'ItemList',
          'image': this.cover,
          'name': this.displayLocaleTitle(this.scenarioData) || this.title,
          'url': this.canonicalURL,
          'description': this.description,
          'itemListElement': []
        }
        this.scenarioData.official_playlists.forEach((oPlaylist, index) => {
          scenarioStructuredData.itemListElement.push({
            'position': index,
            '@type': 'ListItem',
            '@id': `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/playlists/${oPlaylist.playlist.id}`,
            'url': `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/playlists/${oPlaylist.playlist.id}`,
            'image': oPlaylist && oPlaylist.playlist && oPlaylist.playlist.file_link,
            'name': this.displayScenarioPlaylistLocaleTitle(oPlaylist),
            'description': oPlaylist.description
          })
        })
        structuredData.push(scenarioStructuredData)
      }
      return structuredData
    }
  },
  watch: {
    // 'locale'() {}
  },
  created() {
  },
  mounted() {
    this.getScenario()
  },
  beforeDestroy() {
  },
  updated() {},
  methods: {
    displayLocaleTitle,
    displayLocaleDescription,
    displayScenarioPlaylistLocaleTitle,
    displayPlaylistLocaleTitle,
    async getScenario() {
      const MuzikListenV4Reg = /^[A-F\d]{8}-[A-F\d]{4}-4[A-F\d]{3}-[89AB][A-F\d]{3}-[A-F\d]{12}/gi
      const MuzikListenV3Reg = /^[A-Z\d]{5,6}/gi
      const unparsedIdStr = this.$route.params.id.replace('.html', '') // external share link
      let parsedIdStr = ''
      if (MuzikListenV4Reg.test(unparsedIdStr)) {
        parsedIdStr = unparsedIdStr.match(MuzikListenV4Reg)[0] // MUZIK LISTEN v4 (UUID.v4) id format
      } else if (MuzikListenV3Reg.test(unparsedIdStr)) {
        parsedIdStr = unparsedIdStr.match(MuzikListenV3Reg)[0] // 'MUZIK LISTEN v3 id format
      } else {
        parsedIdStr = unparsedIdStr
      }
      const scenarioId = parsedIdStr
      const scenarioRewrite = unparsedIdStr.replace(parsedIdStr, '')
      let res = await ScenarioService.getScenario(scenarioId)
      if (res) {
        this.scenarioData = res
        this.loaded = true
        if (this.scenarioData.id !== unparsedIdStr) {
          // rewrite old version id
          this.$router.replace({ params: { id: this.scenarioData.id } })
        }
      } else {
        this.scenarioData = null
        this.loaded = true
        if (scenarioRewrite) {
          const queryKeyword = scenarioRewrite.split('-').join(' ')
          this.$router.push({
            name: 'search',
            query: {
              type: 'all',
              q: queryKeyword
            }
          })
        }
      }
    },
    toggleShareModal() {
      this.shareModal = true
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/scss/variables.scss';
@import '@/assets/scss/component/button.scss';

.muzik-scenario{
  // padding: 0 $padding-lg-grid-x1;
  .breadcrumbs{
    padding: 24px 0;
  }
  .pgnnp-header {
    margin-top: 24px;
  }
  .scenario-banner{
    width: 100%;
    position: relative;
    padding-top: 25.5%;
    border-radius: 12px;
    overflow: hidden;
    margin: 12px 0 48px;
    img{
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      margin: auto;
      width: auto;
      height: 100%;
    }
  }
  .scenario-content{
    position: relative;
    >.title{
      font-size: 36px;
      font-weight: 500;
      line-height: 1.5;
      color: $grey-50;
      margin: 0 0 16px;
      padding-right: 48px;
    }
    .option{
      position: absolute;
      top: 9px;
      right: 0;
      cursor: pointer;
      i{
        font-size: 36px;
        color: #9b9b9b;
      }
    }
    >.desp{
      font-size: 16px;
      font-weight: bold;
      line-height: 1.6;
      color: $grey-9b;
      margin: 0;
      padding-bottom: 36px;
      a {
        color: $link-hover;
        text-decoration: none;
      }
    }
  }
  .player-list{
    // padding: 16px 0;
    ul{
      list-style: none;
      margin: 0 -21px;
      padding: 0;
      li{
        display: inline-block;
        width: 20%;
        float: left;
        padding: 0 21px;
        text-align: center;
        .block-img{
          position: relative;
          padding-top: 89%;
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          img{
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            margin: auto;
            width: 100%;
            height: auto;
          }
        }
        .title{
          font-size: 20px;
          font-weight: normal;
          line-height: 1.6;
          letter-spacing: -0.1px;
          color: $grey-50;
          margin: 12px 0 20px;
          height: 68px;
          cursor: pointer;
          @include ellipsis-with-lines(2);
        }
      }
      &:after{
        content: "";
        display: block;
        clear: both;
      }
    }
  }
  .loader-container{
    text-align: center;
    .loader{
      margin-bottom: 40px;
    }
  }
  .pgnnp-header {}
  .pgnnp-footer {
    margin-bottom: 24px;
  }
}

@media only screen and (min-width: 1440px) {
  // As same as default
}

@media only screen and (max-width: 1439.98px) {
  .muzik-scenario{
    // padding: 0 $padding-md-grid-x1;
    .scenario-banner{
      padding-top: 27%;
      margin: 12px 0 36px;
    }
    .scenario-content{
      .option{
        top: 9px;
        i{
          font-size: 30px;
        }
      }
      >.desp{
        padding: 18px 0 36px;
      }
    }
    .player-list{
      ul{
        margin: 0 -15px;
        li{
          width: 16.66%;
          padding: 0 15px;
          .title{
            font-size: 16px;
            font-weight: bold;
            color: $grey-7d;
            height: 72px;
            -webkit-line-clamp: 3;
          }
        }
      }
    }
  }
}

@media only screen and (max-width: 1023.98px) {
  .muzik-scenario{
    // padding: 0 $padding-sm-grid-x1;
    .scenario-banner{
      padding-top: 26.5%;
      margin: 0 0 36px;
    }
    .player-list{
      ul{
        margin: 0 -8px;
        li{
          padding: 0 8px;
        }
      }
    }
  }
}

@media only screen and (max-width: 767.98px) {
  .muzik-scenario{
    .scenario-banner{
      padding-top: 41.5%;
      margin: 0 0 16px;
    }
    .scenario-content{
      position: relative;
      >.title{
        font-size: 20px;
        line-height: 1.6;
        text-align: center;
        margin: 0;
        padding-right: 0;
      }
      .option{
        width: 24px;
        top: initial;
        bottom: 24px;
        right: 0;
        left: 0;
        text-align: center;
        margin: auto;
        i{
          font-size: 24px;
        }
      }
      .desp{
        font-size: 14px;
        font-weight: 500;
        line-height: normal;
        padding-bottom: 72px;
      }
    }
    .player-list{
      padding: 0;
      ul{
        margin: 0 -8px;
        li{
          width: 50%;
          padding: 0 8px;
          .title{
            -webkit-line-clamp: 1;
            height: 24px;
            margin: 8px 0 24px;
          }
        }
      }
    }
  }
}
@media only screen and (max-width: 368.98px) {
  .pgnnp-header {
    margin: 0 -24px;
  }
  .pgnnp-footer {
    margin: 0 -24px 24px;
    margin-bottom: 24px;
  }
}
</style>
